import {produce} from 'immer';
import sum from 'lodash/sum';
import isEqual from 'lodash/isEqual';
import partition from 'lodash/partition';
import {VNode} from 'snabbdom';
import {ParamComponent} from 'panel';
import isNumber from 'lodash/isNumber';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {
  CustomMultiAttribution,
  FreePlanAllowedMultiAttributionTypes,
  MultiAttribution,
  MultiAttributionCancelEvent,
  MultiAttributionChangeEvent,
  MultiAttributionDeleteEvent,
  MultiAttributionSection,
  MultiAttributionSectionItem,
  MultiAttributionType,
  MultiAttributionTypeList,
  MultiAttributionWeights,
  RemoveAttributionValue,
} from 'common/widgets/multi-attribution-menu/types';
import {AttributionTypeToSpec, CustomDefaultWeights} from 'common/widgets/multi-attribution-menu/constants';
import {InputChangeEvent} from 'common/components/input/types';
import {InputComponent} from 'common/components/input';
import {parseNumber} from 'common/util';
import {reporter} from 'common/report/error-reporting';
import {DisplaySize} from 'common/components/tooltip/types';

import {template} from './template';
import css from './index.inline.styl';

type MultiAttributionMenuState = {
  focusedAttributionType: MultiAttributionType;
  stagedMultiAttribution: MultiAttribution;
  invalidWeightsText: string | null;
  lastFocusedWeightIndex: number;
};

type MultiAttributionMenuParams = {
  multiAttribution: MultiAttribution;
  metricIndex: number;
  handleCancel?: (ev: MultiAttributionCancelEvent) => void;
  handleChange?: (ev: MultiAttributionChangeEvent) => void;
  handleDelete?: (ev: MultiAttributionDeleteEvent) => void;
  isSupportedMultiAttributionType: (attributionType: MultiAttributionType) => boolean;
  showMultiAttributionType?: (attributionType: MultiAttributionType) => boolean;
  projectIsFreePlan?: boolean;
};

type MultiAttributionMenuJsxProps = {
  params?: MultiAttributionMenuParams;
};

export class MultiAttributionMenu extends ParamComponent<MultiAttributionMenuParams, MultiAttributionMenuState> {
  _tooltipPositionParentElement: HTMLElement;

  get config() {
    return Object.assign(super.config, {
      css,
      template,
      defaultState: {},
      useShadowDom: true,
      params: {
        multiAttribution: {
          type: Object,
          required: true,
        },
        metricIndex: {
          type: Number,
          required: true,
        },
        handleCancel: Function,
        handleChange: Function,
        handleDelete: Function,
        isSupportedMultiAttributionType: Function,
        showMultiAttributionType: Function,
        projectIsFreePlan: Boolean,
      },
      hooks: {
        preUpdate: (_newState, newParams?: MultiAttributionMenuParams) => {
          if (newParams && !isEqual(newParams.multiAttribution, this.params.multiAttribution)) {
            this.handleParamsUpdate(newParams);
          }
        },
      },
    });
  }

  get focusedAttributionIsDisabled(): boolean {
    const sections = this.getAttributionTypeSections();
    const focusedSection = sections[0].items.find((items) => items.value === this.state.focusedAttributionType);
    return focusedSection?.isDisabled;
  }

  connectedCallback() {
    this.handleParamsUpdate(this.params);
    super.connectedCallback();
  }

  handleParamsUpdate(params: MultiAttributionMenuParams): void {
    this.update({
      stagedMultiAttribution: params.multiAttribution,
    });

    // Handle setting the default focused section attribution type so the DropMenu
    // is already open to the current selected attribution type.
    const setDefaultFocusedAttributionType = !this.state.focusedAttributionType;
    if (setDefaultFocusedAttributionType) {
      this.update({
        focusedAttributionType: params.multiAttribution.type,
      });
    }
  }

  attributionTypeIsEnabledForProject(attributionType: MultiAttributionType): boolean {
    if (typeof this.params.projectIsFreePlan !== `boolean`) {
      reporter.error(`attributionTypeIsEnabledForProject: this.params.projectIsFreePlan should be type boolean`, {
        'this.params.projectIsFreePlan': this.params.projectIsFreePlan,
      });
    }
    return !this.params.projectIsFreePlan || FreePlanAllowedMultiAttributionTypes.includes(attributionType);
  }

  getAttributionTypeSections(): MultiAttributionSection[] {
    const multiAttributionItems: MultiAttributionSectionItem[] = MultiAttributionTypeList.filter((attributionType) =>
      this.params.showMultiAttributionType ? this.params.showMultiAttributionType(attributionType) : true,
    ).map((attributionType) => {
      const isSupportedMultiAttributionType = this.params.isSupportedMultiAttributionType(attributionType);
      // Check for enterprise only perms
      const isEnabledForProject = this.attributionTypeIsEnabledForProject(attributionType);

      return {
        icon: AttributionTypeToSpec[attributionType].icon,
        label: AttributionTypeToSpec[attributionType].label,
        value: attributionType,
        isSelected: attributionType === this.state.stagedMultiAttribution?.type,
        isDisabled: !isSupportedMultiAttributionType || !isEnabledForProject,
        tooltip:
          !isSupportedMultiAttributionType && isEnabledForProject
            ? {
                text: `This attribution model is not supported with your current count type.`,
                size: DisplaySize.Small,
              }
            : null,
      };
    });

    const [predefinedItems, customItems] = partition(
      multiAttributionItems,
      (item) => item.value !== MultiAttributionType.Custom,
    );

    const removeAttributionItem: MultiAttributionSectionItem = {
      description: null,
      icon: `x`,
      isSelected: false,
      label: `Remove Attribution`,
      value: RemoveAttributionValue,
      // TODO: need this noop to prevent positioning issue with context panel DropMenu when initially opening the
      // the select menu.
      key: Date.now(),
    };
    return [
      {
        items: [...predefinedItems],
      },
      {
        items: [...customItems, removeAttributionItem],
      },
    ];
  }

  onRemoveAttribution() {
    this.dispatchDelete();
  }

  onAttributionTypeChange(newAttributionType: MultiAttributionType): void {
    this.updateStagedAttribution((draft) => {
      const oldAttributionType = draft.type;
      draft.type = newAttributionType;

      if (draft.type === MultiAttributionType.Custom) {
        const oldWeights = AttributionTypeToSpec[oldAttributionType].weights;
        draft.weights = oldWeights
          ? {...oldWeights}
          : // Default weights for custom model if change from a model without first/middle/last inputs
            CustomDefaultWeights;
        draft.name = this.getDefaultCustomName();
      } else {
        draft.weights = AttributionTypeToSpec[draft.type].weights;
      }
    });
  }

  onCustomNameChange(ev: InputChangeEvent): void {
    this.updateStagedAttribution((draft) => {
      if (draft.type === MultiAttributionType.Custom) {
        draft.name = ev.detail.value || this.getDefaultCustomName();
      } else {
        reporter.error(`onCustomNameChange: unexpected MultiAttributionType`, {type: draft.type});
      }
    });
  }

  getDefaultCustomName(): string {
    return `Custom model ${this.params.metricIndex + 1}`;
  }

  setTooltipPositionParentElement(vnode: VNode) {
    this._tooltipPositionParentElement = vnode.elm as HTMLElement;
  }
  getTooltipPositionParentElement() {
    return this._tooltipPositionParentElement;
  }

  getWeightsForAttributionType(attributionType: MultiAttributionType): MultiAttributionWeights | undefined {
    return AttributionTypeToSpec[attributionType].weights;
  }

  getWeights(): MultiAttributionWeights {
    const {stagedMultiAttribution} = this.state;
    return stagedMultiAttribution.type === MultiAttributionType.Custom
      ? stagedMultiAttribution.weights
      : this.getWeightsForAttributionType(stagedMultiAttribution.type);
  }

  onWeightChange({ev, weightIndex}: {ev: CustomEvent & {target: InputComponent}; weightIndex: number}): void {
    const newWeight = parseNumber(ev.target.value);

    const oldWeights = this.getWeights();
    const newWeights: number[] = [oldWeights.first, oldWeights.middle, oldWeights.last];
    newWeights[weightIndex] = newWeight;

    let invalidWeightsText = null;
    // Every individual value must be valid
    if (newWeights.some((weight) => !isNumber(weight) || weight < 0 || weight > 100)) {
      invalidWeightsText = `Weight values must be between 0 and 100.`;
    }
    // And the total must add up to exactly 100
    else if (sum(newWeights) !== 100) {
      invalidWeightsText = `Attribution does not equate to 100%. Please update the attribution percentages to generate a result.`;
    }

    this.update({
      invalidWeightsText,
      lastFocusedWeightIndex: weightIndex,
    });
    this.updateStagedAttribution((draft) => {
      const partialUpdate: Partial<CustomMultiAttribution> = {
        weights: {
          first: newWeights[0],
          middle: newWeights[1],
          last: newWeights[2],
        },
      };
      if (draft.type !== MultiAttributionType.Custom) {
        partialUpdate.type = MultiAttributionType.Custom;
        partialUpdate.name = this.getDefaultCustomName();
      }
      Object.assign(draft, partialUpdate);
    });
  }

  updateFocusedAttributionType(item: MultiAttributionSectionItem): void {
    this.update({
      // Handle the Remove Attribution button case
      focusedAttributionType: item.value === RemoveAttributionValue ? null : item.value,
    });
  }

  updateStagedAttribution(updateFn: (_: MultiAttribution) => void) {
    this.update({
      stagedMultiAttribution: produce(this.state.stagedMultiAttribution, updateFn),
    });
  }

  dispatchChange() {
    const event: MultiAttributionChangeEvent = new CustomEvent(`change`, {
      detail: {multiAttribution: this.state.stagedMultiAttribution},
    });
    this.params?.handleChange(event);
  }

  dispatchCancel() {
    const event: MultiAttributionCancelEvent = new CustomEvent(`cancel`, {});
    this.params?.handleCancel(event);
  }

  dispatchDelete() {
    const event: MultiAttributionDeleteEvent = new CustomEvent(`delete`, {});
    this.params?.handleDelete(event);
  }
}

export const MultiAttributionMenuEl = defineAndMakeJsxFactory<MultiAttributionMenuJsxProps>(
  `mp-multi-attribution-menu`,
  MultiAttributionMenu,
);
export const MultiAttributionMenuReact = panel2react<MultiAttributionMenuJsxProps>(
  `mp-multi-attribution-menu`,
  MultiAttributionMenu,
);
